import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import VLFCard from "@/components/Card";
import MainActions from "@/components/MainActions";
import MatchSportingConfig, {
	defaultSportingPeriods,
	sportingPeriodsError,
} from "@/components/MatchSportingConfig";
import VLFSelectNew from "@/components/Input/SelectNew";

import {useCategories} from "@/utils/hooks";
import {CompetitionAPI} from "@/api/competition";
import {CompetitionSeasonAPI} from "@/api/competitionSeason";
import {SeasonAPI} from "@/api/season";

import ArrowBack from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {checkRequiredFields, getTextField} from "@/utils/functions";
import {
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
	REQUIRED_FIELD,
} from "@/utils/constants";

import "./index.scss";

const HandleCompetitionSeasonPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({
		defaultSportingPeriods: defaultSportingPeriods(),
		defaultAllowReentry: false,
	});

	const {categories} = useCategories({formatted: true, handleNotification});
	const [competitions, setCompetitions] = useState([]);
	const [seasons, setSeasons] = useState([]);

	const getCompetitions = async () => {
		try {
			const response = await CompetitionAPI.combo();
			setCompetitions(
				response.map((c) => ({
					value: c.id,
					label: c.name,
					competitionType: c.competitionType,
				})),
			);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getSeasons = async () => {
		try {
			const response = await SeasonAPI.allSeasons();
			setSeasons(response.map((s) => ({value: s.id, label: s.name})));
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getCompetitionSeason = async () => {
		setLoading(true);
		try {
			const response = await CompetitionSeasonAPI.getById(id);
			response.currentCompetition = response.competition
				? {value: response.competition.id, label: response.competition.name}
				: null;
			response.currentSeason = response.season
				? {value: response.season.id, label: response.season.name}
				: null;
			response.currentCategory = response.playerCategoryId
				? {value: response.playerCategoryId, label: response.playerCategory}
				: null;
			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const saveCompetitionSeason = async () => {
		const requiredFields = ["currentCompetition", "currentSeason"];
		const errors = checkRequiredFields(requiredFields, fields);
		setErrors(errors);

		const sportingError = sportingPeriodsError(fields.defaultSportingPeriods);
		if (sportingError) {
			handleNotification(sportingError, "error");
			return;
		}
		if (!errors.length) {
			const body = {
				...fields,
				defaultSportingPeriods:
					fields.defaultSportingPeriods?.map((p, i) => ({
						number: i + 1,
						durationMinutes: Number(p.durationMinutes),
						isExtraTime: Boolean(p.isExtraTime),
					})) ?? null,
				defaultAllowReentry: Boolean(fields.defaultAllowReentry),
				playerCategoryId: fields.currentCategory?.value || null,
				competitionId: fields.currentCompetition?.value,
				seasonId: fields.currentSeason?.value,
			};
			setLoading(true);
			try {
				edit
					? await CompetitionSeasonAPI.update(id, body)
					: await CompetitionSeasonAPI.create(body);
				navigate("/competitions");
				handleNotification(SUCCESSFUL_OPERATION, "success");
			} catch (error) {
				handleNotification(
					error?.response?.status === 409
						? "Esiste già un’edizione di questa competizione per la stagione selezionata. Apri l’edizione esistente per modificarla."
						: error?.response?.data?.error || ERROR_OPERATION,
					"error",
				);
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		getCompetitions();
		getSeasons();
	}, []);

	useEffect(() => {
		if (edit) {
			getCompetitionSeason();
		}
	}, [edit]);

	const handleSubmit = (event) => {
		event.preventDefault();
		saveCompetitionSeason();
	};

	const goBack = () => {
		navigate("/competitions");
	};

	const mainActions = [
		{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
		{icon: <SaveIcon />, title: "Salva", onClick: handleSubmit},
	];

	const label = fields.currentCompetition
		? fields.currentCompetition.label +
			" " +
			(fields.currentSeason?.label || "")
		: "";

	const breadcrumbsElements = [{label: "Competizioni", path: "/competitions"}];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{label, path: "/competitions/" + id},
		);
	}

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={edit ? "Modifica" : "Nuova competizione"}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							openable={false}
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-sm-12 col-md-6 padding-small">
										<VLFSelectNew
											options={competitions}
											value={fields.currentCompetition}
											onChange={(newValue) => {
												setErrors([]);
												setFields({...fields, currentCompetition: newValue});
											}}
											label="Competizione"
											required
											helperText={
												errors.indexOf("currentCompetition") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("currentCompetition") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6 padding-small">
										<VLFSelectNew
											label="Categoria (facoltativa)"
											options={categories}
											value={fields.currentCategory || null}
											isClearable
											onChange={(currentCategory) =>
												setFields({...fields, currentCategory})
											}
										/>
									</div>
									<div className="col-sm-12 col-md-6 padding-small">
										<VLFSelectNew
											options={seasons}
											sortOrder="descend"
											value={fields.currentSeason}
											onChange={(newValue) => {
												setErrors([]);
												setFields({...fields, currentSeason: newValue});
											}}
											label="Stagione"
											required
											helperText={
												errors.indexOf("currentSeason") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("currentSeason") !== -1}
										/>
									</div>
									<div className="col-sm-12 col-md-6 padding-small">
										<p className="mb-0">
											La categoria scelta sarà applicata anche alle partite già
											presenti nell’edizione.
											<br />
											Rimuoverla conserva le categorie delle singole partite.
										</p>
									</div>
									<div className="col-sm-12 col-md-12 padding-small">
										{getTextField({
											...defaultTextField,
											fieldKey: "notes",
											label: "Note",
											multiline: true,
											rows: 3,
										})}
									</div>
								</div>
							}
						/>
					</div>
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Regole predefinite delle partite"
							openable={false}
							content={
								<>
									<p>
										Le nuove partite erediteranno queste impostazioni,
										modificabili sulla singola gara. Le partite già presenti non
										vengono modificate. Senza un default dei tempi, il modulo
										propone 2 tempi da 45 minuti.
									</p>
									<MatchSportingConfig
										periods={fields.defaultSportingPeriods}
										allowReentry={fields.defaultAllowReentry}
										onChange={(value) =>
											setFields((prev) => ({
												...prev,
												...("sportingPeriods" in value
													? {defaultSportingPeriods: value.sportingPeriods}
													: {}),
												...("allowReentry" in value
													? {defaultAllowReentry: value.allowReentry}
													: {}),
											}))
										}
									/>
								</>
							}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default HandleCompetitionSeasonPage;
